import { Injectable } from '@angular/core';
import { Apollo, QueryRef } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	AddFlightAppApiKeyDocument,
	AddReleaseNoteDocument,
	CreateApiKeyDocument,
	CreateIcsIdDocument,
	CreateIcsIdUserDocument,
	DeleteFlightAppApiKeyDocument,
	DeleteReleaseNoteDocument,
	FlyabilityDefaultsDocument,
	FlyabilityThresholdsDocument,
	FlyabilityThresholdInput,
	GetAllReleaseNotesDocument,
	GetDefaultLocationDocument,
	GetFlightDocument,
	GetLocationsDocument,
	GetReleaseNoteDocument,
	GetReleaseNotesCountDocument,
	GetReleaseNotesDocument,
	GetSubscriptionDocument,
	GetTenantSettingDocument,
	GetTenantSettingMyFlightDocument,
	GetUpUrlDocument,
	GetUserDocument,
	InputDisplayedColumns,
	InputLocationOptions,
	InputOperatorSettingsBackup,
	InputOperatorSettingsCrew,
	InputOperatorSettingsFlights,
	InputOperatorSettingsMyFlight,
	InputOperatorSettingsNotifications,
	InputOperatorSettingsSms,
	InputOperatorSettingsTrajectoryProfile,
	InputOperatorSettingsWeatherSource,
	InputReleaseNote,
	InputTenantSetting,
	MarkReleaseNoteAsReadDocument,
	RefreshTenantSubscriptionUsedSmsDocument,
	SubscriptionSmsIgnoreWarningDocument,
	UpdateFlyabilitySettingsDocument,
	UpdateTenantOperatorSettingsWeatherSourceDocument,
	WeatherSourceDefaultsDocument,
	ToggleTenantOperatorSettingsBookingsShowPaymentsDocument,
	ToggleTenantWAfallbackSmsDocument,
	UpdateTenantMessageRetentionDocument,
	MessageRetentionImpactDocument,
	MessageRetentionStatusDocument,
	InputMessageRetention,
	UpdateReleaseNoteDocument,
	UpdateTenantCallbackUrlDocument,
	UpdateTenantLocationOptionsDocument,
	UpdateTenantOperatorSettingsBackupDocument,
	UpdateTenantOperatorSettingsBookingsChildAgeDocument,
	UpdateTenantOperatorSettingsBookingsFlightOptionsCountDocument,
	UpdateTenantOperatorSettingsBookingsSourcesDocument,
	UpdateTenantOperatorSettingsCrewDocument,
	UpdateTenantOperatorSettingsDisabledPaymentTypesDocument,
	UpdateTenantOperatorSettingsDisplayedColumnsDocument,
	UpdateTenantOperatorSettingsFlightsCancelReasonsDocument,
	UpdateTenantOperatorSettingsFlightsDocument,
	UpdateTenantOperatorSettingsMyFlightDocument,
	UpdateTenantOperatorSettingsNotificationsDocument,
	UpdateTenantOperatorSettingsSmsDocument,
	UpdateTenantOperatorSettingsTrajectoryProfileDocument,
	UpdateTenantOperatorSettingsTwoFaDocument,
	UpdateTenantSettingDocument,
} from '../../graphql/generated/gen-types';
import { AuthenticationService } from '../authentication/authentication.service';

@Injectable({
	providedIn: 'root',
})
export class TenantSettingService {
	currentUser;

	constructor(
		private apollo: Apollo,
		private authenticationService: AuthenticationService,
	) {
		this.authenticationService.currentUser.subscribe((user) => {
			this.currentUser = user;
		});
	}

	public getTenantSetting(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetTenantSettingDocument,
		});
	}
	public getAllReleaseNotes(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetAllReleaseNotesDocument,
		});
	}
	public getReleaseNote(releaseNoteId: String): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetReleaseNoteDocument,
			variables: {
				releaseNoteId,
			},
		});
	}
	public getReleaseNotes(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetReleaseNotesDocument,
		});
	}
	public getReleaseNotesCount(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetReleaseNotesCountDocument,
		});
	}
	public getTenantSettingMyFlight(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetTenantSettingMyFlightDocument,
		});
	}

	public getSubscription(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetSubscriptionDocument,
		});
	}

	public getUpUrl(bookingId: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetUpUrlDocument,
			variables: {
				bookingId,
			},
		});
	}

	public getDefaultLocation(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetDefaultLocationDocument,
		});
	}

	public updateTenantLocationOptions(options: InputLocationOptions): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantLocationOptionsDocument,
				variables: {
					options,
				},
				refetchQueries: [{ query: GetLocationsDocument }, { query: GetDefaultLocationDocument }],
			})
			.pipe(map((result: any) => result.data));
	}

	public updateTenantOperatorSettingsBackup(settings: InputOperatorSettingsBackup): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsBackupDocument,
				variables: {
					settings,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public updateTenantOperatorSettingsDisplayedColumns(displayedColumns: InputDisplayedColumns): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsDisplayedColumnsDocument,
				variables: {
					displayedColumns,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}

	public getWeatherSourceDefaults(): QueryRef<any> {
		return this.apollo.watchQuery<any>({
			query: WeatherSourceDefaultsDocument,
		});
	}

	public getFlyabilityDefaults(): QueryRef<any> {
		return this.apollo.watchQuery<any>({
			query: FlyabilityDefaultsDocument,
		});
	}

	// effective (merged) limits — settings editor only, deliberately not part of getTenantSetting
	public getFlyabilityThresholds(): QueryRef<any> {
		return this.apollo.watchQuery<any>({
			query: FlyabilityThresholdsDocument,
		});
	}

	// The submitted list is the complete override set: omit a parameter to reset it to the
	// platform default, submit an empty list to reset all of them (104).
	public updateFlyabilitySettings(thresholds: FlyabilityThresholdInput[], enabled: boolean): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateFlyabilitySettingsDocument,
				variables: {
					thresholds,
					enabled,
				},
				refetchQueries: [{ query: FlyabilityThresholdsDocument }, { query: GetDefaultLocationDocument }],
			})
			.pipe(map((result: any) => result.data));
	}

	public updateTenantOperatorSettingsWeatherSource(settings: InputOperatorSettingsWeatherSource): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsWeatherSourceDocument,
				variables: {
					settings,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}

	public updateTenantOperatorSettingsSms(settings: InputOperatorSettingsSms): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsSmsDocument,
				variables: {
					settings,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public updateTenantOperatorSettingsMyFlight(settings: InputOperatorSettingsMyFlight): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsMyFlightDocument,
				variables: {
					settings,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public updateTenantOperatorSettingsFlights(settings: InputOperatorSettingsFlights, updateHours: boolean = false): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsFlightsDocument,
				variables: {
					settings,
					updateHours,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public updateTenantOperatorSettingsCrew(settings: InputOperatorSettingsCrew): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsCrewDocument,
				variables: {
					settings,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public updateTenantOperatorSettingsNotifications(settings: InputOperatorSettingsNotifications): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsNotificationsDocument,
				variables: {
					settings,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public updateTenantOperatorSettingsBookingsChildAge(childAge: number): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsBookingsChildAgeDocument,
				variables: {
					childAge,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public updateTenantOperatorSettingsBookingsFlightOptionsCount(flightOptionsCount: number): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsBookingsFlightOptionsCountDocument,
				variables: {
					flightOptionsCount,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public updateTenantOperatorSettingsBookingsSources(sources: string[]): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsBookingsSourcesDocument,
				variables: {
					sources,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public updateTenantOperatorSettingsFlightsCancelReasons(cancelReasons: string[]): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsFlightsCancelReasonsDocument,
				variables: {
					cancelReasons,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public updateTenantOperatorSettingsDisabledPaymentTypes(disabledPaymentTypes: [string]): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsDisabledPaymentTypesDocument,
				variables: {
					disabledPaymentTypes,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public toggleTenantWAfallbackSMS(): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: ToggleTenantWAfallbackSmsDocument,
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public updateTenantMessageRetention(messageRetention: InputMessageRetention): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantMessageRetentionDocument,
				variables: { messageRetention },
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public messageRetentionImpact(messageRetention: InputMessageRetention): Observable<any> {
		return this.apollo
			.query<any>({
				query: MessageRetentionImpactDocument,
				variables: {
					closedBookingsYears: messageRetention.closedBookingsYears,
					crewYears: messageRetention.crewYears,
				},
				fetchPolicy: 'network-only',
			})
			.pipe(map((result: any) => result.data.messageRetentionImpact));
	}
	public messageRetentionStatus(): Observable<any> {
		return this.apollo
			.query<any>({
				query: MessageRetentionStatusDocument,
				fetchPolicy: 'network-only',
			})
			.pipe(map((result: any) => result.data.messageRetentionStatus));
	}
	public toggleTenantOperatorSettingsBookingsShowPayments(): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: ToggleTenantOperatorSettingsBookingsShowPaymentsDocument,
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public updateTenantOperatorSettingsTrajectoryProfile(
		settings: InputOperatorSettingsTrajectoryProfile[],
		flightId: string,
	): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsTrajectoryProfileDocument,
				variables: {
					settings,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }, { query: GetFlightDocument, variables: { flightId } }],
			})
			.pipe(map((result: any) => result.data));
	}

	public subscriptionSmsIgnoreWarning(ignore: Boolean): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SubscriptionSmsIgnoreWarningDocument,
				variables: {
					ignore,
				},
				refetchQueries: [{ query: GetSubscriptionDocument }],
			})
			.pipe(map((result: any) => result.data));
	}

	public addReleaseNote(releaseNote: InputReleaseNote): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: AddReleaseNoteDocument,
				variables: {
					releaseNote,
				},
				refetchQueries: [{ query: GetAllReleaseNotesDocument }],
			})
			.pipe(map((result: any) => result.data));
	}

	public updateReleaseNote(id: string, releaseNote: InputReleaseNote): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateReleaseNoteDocument,
				variables: {
					id,
					releaseNote,
				},
				refetchQueries: [{ query: GetAllReleaseNotesDocument }],
			})
			.pipe(map((result: any) => result.data));
	}

	public deleteReleaseNote(id: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: DeleteReleaseNoteDocument,
				variables: {
					id,
				},
				refetchQueries: [{ query: GetAllReleaseNotesDocument }],
			})
			.pipe(map((result: any) => result.data));
	}

	public markReleaseNoteAsRead(releaseNoteIds: string[]): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: MarkReleaseNoteAsReadDocument,
				variables: {
					releaseNoteIds,
				},
				refetchQueries: [{ query: GetReleaseNotesCountDocument }, { query: GetReleaseNotesDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public createIcsId(): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: CreateIcsIdDocument,
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public createApiKey(): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: CreateApiKeyDocument,
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public createIcsIdUser(userId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: CreateIcsIdUserDocument,
				variables: {
					userId,
				},
				refetchQueries: [{ query: GetUserDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public addFlightAppApiKey(key: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: AddFlightAppApiKeyDocument,
				variables: {
					key,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public deleteFlightAppApiKey(key: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: DeleteFlightAppApiKeyDocument,
				variables: {
					key,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public refreshTenantSubscriptionUsedSms(): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: RefreshTenantSubscriptionUsedSmsDocument,
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public updateTenantCallbackUrl(callbackUrl: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantCallbackUrlDocument,
				variables: {
					callbackUrl,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
	public updateTenantOperatorSettingsTwoFA(twoFAEnabled: boolean): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantOperatorSettingsTwoFaDocument,
				variables: {
					twoFAEnabled,
				},
				refetchQueries: [{ query: GetTenantSettingDocument }],
			})
			.pipe(map((result: any) => result.data));
	}

	public updateTenantSettings(tenantId: string, settings: InputTenantSetting): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: UpdateTenantSettingDocument,
				variables: {
					tenantId,
					settings,
				},
				refetchQueries: [{ query: GetSubscriptionDocument }],
			})
			.pipe(map((result: any) => result.data));
	}
}
